import React, {useRef, useCallback, useEffect} from 'react';
import gsap from 'gsap';

import {whenVisible} from 'polyphony/dom';
import {prefixCSS} from 'polyphony/css';
import {executeAfter} from 'polyphony/util';

const EasingAnimation = {
    pre: {
        autoAlpha: 0,
        yPercent: 100
    },
    from: prefixCSS({
        backfaceVisibility: 'hidden',
	perspective: 1000,
        height: '100%',
        width: '100%',
        opacity: 0
    }),
    to: {
        yPercent: 0,
        autoAlpha: 1
    },
    time: 0.75
};

const EasingContext = React.createContext(() => undefined);

export const EaseIn = ({children, style,...props}) => {
    const didAnimateRef = useRef(false); // did we animate?
    const savedDelayRef = useRef(false);
    const needsAnimationRef = useRef(false); // did we try to animate at an inopportune time and need to delay?
    const canAnimate = useRef(false); // can we animate?

    const div = useRef(); // the div we're animating

    const animate = useCallback((delay = 0) => {
        if (!didAnimateRef.current) { // If we haven't animated
            if (div.current && canAnimate.current) { // and we have a div, and it's onscreen
                didAnimateRef.current = true;
                const doIt = () => {
                    const el = div.current;
                    gsap.set(el, EasingAnimation.pre);
                    gsap.to(el, EasingAnimation.time, EasingAnimation.to);
                };
                if (delay > 0) executeAfter(doIt, delay * 1000);
                else           doIt();
            } else {
                needsAnimationRef.current = true; // otherwise, we need to defer animation
                savedDelayRef.current = delay;
            }
        }
    }, [needsAnimationRef.current, didAnimateRef.current, div.current]);

    const cancelRef = useRef(null);

    const handleRef = r => {
        div.current = r;
        if (div.current) {
            if (cancelRef.current) cancelRef.current();

            cancelRef.current = whenVisible(div.current, () => {
                canAnimate.current = true;
                if (needsAnimationRef.current) {
                    animate(savedDelayRef.current);
                }
            });
        }
    };

    useEffect(() => () => {
        if (cancelRef.current) {
            cancelRef.current();
            cancelRef.current = null;
        }
    }, []);

    return (
        <EasingContext.Provider value={animate}>
            <div role="none" {...props} ref={handleRef} style={{...style, ...EasingAnimation.from}}>
                {children}
            </div>
        </EasingContext.Provider>
    );
};

EaseIn.displayName = 'EaseIn';

EaseIn.useAnimate = () => React.useContext(EasingContext);

export default EaseIn;

